import { ROLES, ROLE_SUPPORT_ADMIN, ROLE_SUPPORT_USER } from '@ruminati/permissions/roles'
import { type RelationshipT } from '@ruminati/types_v2/relationship'
import { type UserT, type UserWithOrganisationsAndRelationshipsT } from '@ruminati/types_v2/user'
import { defineStore } from 'pinia'
import { computed, watch, ref } from 'vue'

import { CommonApiV2Instance } from '@/services/common'
import {
  sendBroadcastMessageFromTabIfFocused,
  SESSION_CHANGE_REASONS,
} from '@/utilities/broadcaster'
import { getCurrentProviderCookie } from '@/utilities/cookies'
import { rollbar } from '@/utilities/rollbar'

import { useOrganisationStore } from './organisations'

export const useUsersStore = defineStore('users', () => {
  const organisationStore = useOrganisationStore()

  const profile = ref<UserT | undefined>()
  const relationships = ref<null | RelationshipT[]>(null)

  watch(
    () => profile.value,
    (newProfile) => {
      if (newProfile !== undefined) {
        rollbar.configure({
          payload: {
            person: {
              id: newProfile.id,
              email: newProfile.email,
              firstName: newProfile.firstName,
              lastName: newProfile.lastName,
            },
          },
        })
      }
    },
  )

  function setProfile(user: UserT) {
    profile.value = user
  }

  function setRelationships(newRelationships: RelationshipT[]) {
    relationships.value = newRelationships
  }

  function upsertRelationships(newRelationships: Array<RelationshipT>) {
    if (relationships.value === null) {
      relationships.value = newRelationships
    } else {
      const existingMap = new Map(
        relationships.value.map((relationship) => [relationship.id, relationship]),
      )
      for (const relationship of newRelationships) {
        existingMap.set(relationship.id, relationship)
      }
      relationships.value = Array.from(existingMap.values())
    }
  }

  function setPrimaryOrganisationId(primaryOrganisationId: string) {
    if (profile.value === undefined) return
    profile.value = {
      ...profile.value,
      userPreferences: {
        ...profile.value.userPreferences,
        primaryOrganisationId,
      },
    }
  }

  // Used by the router auth guard for the initial bootstrap fetch (before any component mounts).
  // Seeds the TanStack Query cache so components don't double-fetch on first load.
  async function fetchProfile(): Promise<{ success: true; data: UserT } | { success: false }> {
    try {
      const response =
        await CommonApiV2Instance.get('users/me').json<UserWithOrganisationsAndRelationshipsT>()
      if (response.organisations !== undefined) {
        organisationStore.upsertOrganisations(response.organisations)
      }
      if (response.relationships !== undefined) {
        relationships.value = response.relationships
      }
      if (response.pendingOrgInvitations !== undefined) {
        organisationStore.setPendingOrgInvitations(response.pendingOrgInvitations)
      }
      if (profile.value?.id && profile.value?.id !== response.user.id) {
        sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.USER_CHANGED)
      }
      profile.value = response.user
      organisationStore.initializeCurrentOrg(response.user)
      return { success: true, data: response.user }
    } catch {
      return { success: false }
    }
  }
  const userRoleInCurrentOrg = computed(() => {
    if (!profile.value || !relationships.value || !organisationStore.currentOrgId) {
      return undefined
    }
    const matchingRelationship = relationships.value.find((r) => {
      return r.resourceId === organisationStore.currentOrgId && r.recipientId === profile.value!.id
    })
    if (!matchingRelationship) {
      return undefined
    }
    return ROLES.find((r) => r.id === matchingRelationship.roleId)
  })

  const provider = computed(() => getCurrentProviderCookie())

  const relationshipToRuminatiOrg = computed(() => {
    const ruminatiOrg = organisationStore.organisations?.find((o) => o.type === 'ruminati')
    return (
      ruminatiOrg &&
      relationships.value?.find(
        (r) => r.resourceId === ruminatiOrg.id && r.recipientId === profile.value!.id,
      )
    )
  })

  const isSupportAdmin = computed<boolean>(() => {
    return (
      relationshipToRuminatiOrg.value !== undefined &&
      relationshipToRuminatiOrg.value.roleId === ROLE_SUPPORT_ADMIN.id
    )
  })

  const isSupportUser = computed<boolean>(() => {
    return (
      relationshipToRuminatiOrg.value !== undefined &&
      relationshipToRuminatiOrg.value.roleId === ROLE_SUPPORT_USER.id
    )
  })

  function reset() {
    profile.value = undefined
    relationships.value = null
  }

  return {
    profile,
    relationships,

    setProfile,
    setRelationships,
    upsertRelationships,
    setPrimaryOrganisationId,
    fetchProfile,

    userRoleInCurrentOrg,
    provider,
    relationshipToRuminatiOrg,
    isSupportAdmin,
    isSupportUser,

    reset,
  }
})
